import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listRefurbJobs, updateRefurbJob, refurbStats } from "@/lib/refurb.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Play, CheckCircle2, XCircle, Hammer } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/returns-refurb")({ component: RefurbPage });

const STATUS_LABEL: Record<string,string> = {
  queued: "Queued", in_progress: "In progress", restocked: "Restocked",
  scrapped: "Scrapped", cancelled: "Cancelled",
};
const STATUS_VARIANT: Record<string, "secondary"|"default"|"outline"|"destructive"> = {
  queued: "secondary", in_progress: "default", restocked: "outline",
  scrapped: "destructive", cancelled: "outline",
};

function RefurbPage() {
  const qc = useQueryClient();
  const { tenantId, facilityId, customerId } = useScope();
  const listFn = useServerFn(listRefurbJobs);
  const updateFn = useServerFn(updateRefurbJob);
  const statsFn = useServerFn(refurbStats);

  const [status, setStatus] = useState<"queued"|"in_progress"|"restocked"|"scrapped"|"cancelled"|"all">("queued");

  const { data } = useQuery({
    queryKey: ["refurb-jobs", tenantId, facilityId, customerId, status],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null, status } }),
  });
  const { data: stats } = useQuery({
    queryKey: ["refurb-stats", tenantId, facilityId, customerId],
    enabled: !!tenantId,
    queryFn: () => statsFn({ data: { tenantId: tenantId!, facilityId: facilityId ?? null, customerId: customerId ?? null } }),
  });

  const update = useMutation({
    mutationFn: (v: { id: string; status: "queued"|"in_progress"|"restocked"|"scrapped"|"cancelled" }) =>
      updateFn({ data: { id: v.id, status: v.status } }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["refurb-jobs"] });
      qc.invalidateQueries({ queryKey: ["refurb-stats"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader title="Refurb Queue" description="Track items going through refurbishment between receipt and restock." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/returns"><ArrowLeft className="h-4 w-4 mr-1" />Back to returns</Link>
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(["queued","in_progress","restocked","scrapped","cancelled"] as const).map((k) => (
            <Card key={k}>
              <CardContent className="p-3">
                <div className="text-[11px] uppercase text-muted-foreground tracking-wide">{STATUS_LABEL[k]}</div>
                <div className="text-2xl font-semibold tabular-nums">{(stats as any)?.[k] ?? 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="text-xs text-muted-foreground">Filter</div>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {(["queued","in_progress","restocked","scrapped","cancelled"] as const).map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-muted-foreground">
              Total labor: <span className="tabular-nums">{stats?.totalLaborMinutes ?? 0}m</span>
              {" · "}Parts: <span className="tabular-nums">${(((stats?.totalPartsCostCents ?? 0) as number) / 100).toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Created</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-64 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.jobs ?? []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground"><Hammer className="h-4 w-4 inline mr-2" />No refurb jobs.</TableCell></TableRow>}
                {(data?.jobs ?? []).map((j: any) => (
                  <TableRow key={j.id}>
                    <TableCell className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleString()}</TableCell>
                    <TableCell className="font-mono text-xs">{j.sku_id.slice(0, 8)}</TableCell>
                    <TableCell className="text-right tabular-nums">{j.qty}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[j.status]}>{STATUS_LABEL[j.status]}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground truncate max-w-xs">{j.notes ?? "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {j.status === "queued" && (
                          <Button size="sm" variant="outline" onClick={() => update.mutate({ id: j.id, status: "in_progress" })}>
                            <Play className="h-3.5 w-3.5 mr-1" />Start
                          </Button>
                        )}
                        {j.status === "in_progress" && (
                          <>
                            <Button size="sm" onClick={() => update.mutate({ id: j.id, status: "restocked" })}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Restock
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => update.mutate({ id: j.id, status: "scrapped" })}>
                              <XCircle className="h-3.5 w-3.5 mr-1" />Scrap
                            </Button>
                          </>
                        )}
                        {(j.status === "queued" || j.status === "in_progress") && (
                          <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: j.id, status: "cancelled" })}>Cancel</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}